import {
  Card,
  ConfirmationModal,
  Space,
  Tag,
  Text,
  UncontrolledDateRangePicker,
} from '@procurify/ui'

import { parseYYYYMMDD } from '@webapp/core/utils/dates/parseYYYYMMDD/parseYYYYMMDD'
import { useProcurifyIntl } from '@webapp/hooks'
import { ExportTaskListPresenter } from '@webapp/modules/reports/features/list-export-tasks/presenters/ExportTaskListPresenter'
import messages from './messages'
import { useReportExportHistoryView } from './useReportExportHistoryView'

/**
 * Feature Set: Report Export History View (Page)
 *
 * Connects the list-export-tasks query feature to the hook-only
 * delete-export-task command feature through the FS-owned danger
 * ConfirmationModal (ADR-9, BHV-037). Owns the referrer-derived task
 * type filter and the date-range chrome (default last 3 months →
 * tomorrow, BHV-035); the back-breadcrumb reconstruction (BHV-008) lives
 * in ReportExportHistoryPage via `parseExportHistoryReferrer`.
 */
export const ReportExportHistoryView = () => {
  const intl = useProcurifyIntl()

  const { data, states, methods } = useReportExportHistoryView()

  const dateRangeLabel = `${intl.formatDate(
    parseYYYYMMDD(data.dateRange.from)
  )} - ${intl.formatDate(parseYYYYMMDD(data.dateRange.to))}`

  return (
    <>
      <Space size='large'>
        <Card>
          <Card.Content>
            <Space>
              <Space.Item minWidth={260} width='fit-content'>
                <UncontrolledDateRangePicker
                  name='export-history-date-range'
                  fullWidth
                  startDate={parseYYYYMMDD(data.dateRange.from)}
                  endDate={parseYYYYMMDD(data.dateRange.to)}
                  onChange={methods.handleDateRangeChange}
                />
              </Space.Item>

              <Space direction='horizontal' size='small' align='center'>
                <Text bold autoMargin={false}>
                  {intl.formatMessage(messages.showingLabel)}
                </Text>
                <Tag>{dateRangeLabel}</Tag>
              </Space>
            </Space>
          </Card.Content>
        </Card>

        <Card>
          <Card.Content>
            <ExportTaskListPresenter
              typeFilter={data.typeFilter}
              dateRange={data.dateRange}
              onDeleteRequest={methods.handleDeleteRequest}
            />
          </Card.Content>
        </Card>
      </Space>

      <ConfirmationModal
        open={!!states.pendingDeleteTask}
        type='danger'
        header={intl.formatMessage(messages.deleteConfirmationHeader)}
        content={intl.formatMessage(messages.deleteConfirmationContent)}
        confirmButtonText={intl.formatMessage(
          messages.deleteConfirmationConfirmButtonText
        )}
        cancelButtonText={intl.formatMessage(
          messages.deleteConfirmationCancelButtonText
        )}
        onConfirm={methods.handleConfirmDelete}
        handleDismiss={methods.handleDismissDelete}
      />
    </>
  )
}

ReportExportHistoryView.displayName = 'ReportExportHistoryView'
